import { useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import type { SalesChannelResponse } from '../../model/types';

export interface ChannelFormValue {
  code: string;
  name: string;
  active: boolean;
  isDefault: boolean;
  mirrorsChannelCode: string;
}

interface Props {
  open: boolean;
  editing?: SalesChannelResponse;
  channels: SalesChannelResponse[];
  saving: boolean;
  onClose: () => void;
  onSubmit: (value: ChannelFormValue) => void;
}

export function ChannelFormDialog({ open, editing, channels, saving, onClose, onSubmit }: Props) {
  const [value, setValue] = useState<ChannelFormValue>({
    code: '',
    name: '',
    active: true,
    isDefault: false,
    mirrorsChannelCode: '',
  });

  useEffect(() => {
    setValue({
      code: editing?.code ?? '',
      name: editing?.name ?? '',
      active: editing?.active ?? true,
      isDefault: editing?.isDefault ?? false,
      mirrorsChannelCode: editing?.mirrorsChannelCode ?? '',
    });
  }, [editing, open]);

  const others = channels.filter((c) => c.code !== value.code);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editing ? 'Изменить канал' : 'Новый канал продаж'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Код"
            value={value.code}
            disabled={Boolean(editing)}
            onChange={(e) => setValue((v) => ({ ...v, code: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Название"
            value={value.name}
            onChange={(e) => setValue((v) => ({ ...v, name: e.target.value }))}
            fullWidth
          />
          <TextField
            select
            label="Зеркалит канал (общая цена)"
            value={value.mirrorsChannelCode}
            onChange={(e) => setValue((v) => ({ ...v, mirrorsChannelCode: e.target.value }))}
            fullWidth
          >
            <MenuItem value="">— не зеркалит —</MenuItem>
            {others.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={value.active}
                onChange={(e) => setValue((v) => ({ ...v, active: e.target.checked }))}
              />
            }
            label="Активен"
          />
          <FormControlLabel
            control={
              <Switch
                checked={value.isDefault}
                onChange={(e) => setValue((v) => ({ ...v, isDefault: e.target.checked }))}
              />
            }
            label="По умолчанию"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button
          variant="contained"
          disabled={saving || !value.code || !value.name}
          onClick={() => onSubmit(value)}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
